<div
  class="card card-chat-message h-100"
  (paste)="onPaste($event)"
  >
  @if (!attendance) {
    <empty-records-legacy-component
      class="my-auto"
      [message]="'attendance.chat.noActiveChats' | translate"
      [subMessage]="'attendance.chat.noActiveChatsHint' | translate"
      />
  } @else {
    <attendance-chat-header-component
      [attendance]="attendance"
      [aiModule]="activeIAModule"
      [aiActive]="isAIActive()"
      [callModule]="activeWavoipModule"
      [crmIntegration]="!!(hasPipedriveIntegration$ | async)"
      [exporting]="isLoadingExport"
      [panelOpen]="!sidebarRight"
      [searchOpen]="searchOpen"
      [(searchText)]="searchText"
      [searchIndex]="currentSearchResultIndex"
      [searchTotal]="totalSearchResults"
      (action)="onHeaderAction($event)"
      />

    <div class="card-body chat-body p-0 pt-2">
      <chat-messages-component
        #messageContainer
        [attendance]="attendance"
        [messages]="messages"
        [pager]="pagerMessage"
        [whispers]="whispers$ | async"
        (whisperReply)="startWhisperReply($event)"
        (whisperEdit)="startWhisperEdit($event)"
        (messageLoadPage)="loadPage()"
        (messageEditToAttendance)="getMessageEdit($event)"
        (messageReplyToAttendance)="getMessageReply($event)"
        (messageScrollToReply)="scrollToReplyMessage($event)"
        />
      <div class="mb-2 pr-3 form-message">
        @if (isMessageEdit && !isMessageReplyed) {
          <div
            class="reply-container reply-container-edit bg-light p-1 rounded-top border mb-2 mx-1"
            >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <p class="small fw-bold text-muted mb-1">
                  {{ "attendance.chat.editing" | translate }}
                </p>
                <p class="small text-dark text-truncate mb-0">
                  {{ messageEdit.content | truncate: 50 }}
                </p>
              </div>
              <button
                class="btn btn-link text-muted p-0"
                [attr.aria-label]="'attendance.chat.cancelEdit' | translate"
                (click)="cancelEdit()"
                >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>
        }
      </div>
      <div class="mb-2 pr-3 form-message">
        @if (isMessageReplyed && !isMessageEdit) {
          <div
            class="reply-container reply-container-edit bg-light p-1 rounded-top border mb-3 mx-1"
            >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <p class="small fw-bold text-muted mb-1">
                  {{ "attendance.chat.replying" | translate }}
                </p>
                <p class="small text-dark text-truncate mb-0">
                  {{ messageReplyed.content | truncate: 50 }}
                </p>
              </div>
              <button
                class="btn btn-link text-muted p-0"
                [attr.aria-label]="'attendance.chat.cancelReply' | translate"
                (click)="cancelReply()"
                >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Whisper field: its own component, outside canComposeMessage() and never the customer composer. -->
    @if (whisperFieldOpen && attendance?.status !== "CLOSED") {
      <attendance-whisper-field-component
        [attendance]="attendance"
        [replyTo]="whisperReplyTo"
        [editing]="whisperEditing"
        (closed)="closeWhisperField()"
        />
    }

    @if (attendance?.status !== "CLOSED") {
      <div
        class="card-body chat-footer bg-light pb-0"
        [class.pt-2]="!canComposeMessage()"
        >
        <mat-menu id="menu-items" #mediaMenu="matMenu">
          <button mat-menu-item (click)="sendMedia('video')">
            <i class="ri-video-line"></i>
            {{ "attendance.chat.video" | translate }}
          </button>
          <button mat-menu-item (click)="sendMedia('document')">
            <i class="ri-file-line"></i>
            {{ "attendance.chat.document" | translate }}
          </button>
          <button mat-menu-item (click)="sendMedia('image')">
            <i class="ri-image-line"></i>
            {{ "attendance.chat.image" | translate }}
          </button>
          @if (attendance?.channel?.type !== "RCS_API") {
            <button mat-menu-item (click)="sendMedia('audio')">
              <i class="mdi mdi-microphone-outline"></i>
              {{ "attendance.chat.audio" | translate }}
            </button>
            <button mat-menu-item (click)="sendContact()">
              <i class="ri-contacts-line"></i>
              {{ "attendance.chat.contact" | translate }}
            </button>
            <button mat-menu-item (click)="sendLocation()">
              <i class="mdi mdi-map-marker-outline"></i>
              {{ "attendance.chat.location" | translate }}
            </button>
          }
        </mat-menu>

        @if (isHybridSessionDisconnected()) {
          <!-- Hybrid channel: session side down, free-form goes official (SPEC-2026-10-whatsapp-hybrid-channel RF-09) -->
          <div class="alert alert-warning py-1 px-2 mb-2 small" role="alert">
            <i class="mdi mdi-link-variant-off me-1"></i>
            {{ "attendance.chat.hybridSessionDisconnected" | translate }}
          </div>
        }

        @if (canComposeMessage()) {
          <form [formGroup]="formGroup" class="d-block w-100">
            <div class="form-message">
              @if (isRecording) {
                <div class="recording-preview">
                  <p class="audio-time">{{ getFormattedTime() }}</p>

                  <div class="audio-bars">
                    @for (item of waves; track $index) {
                      <span [ngStyle]="{ '--level': item }"></span>
                    }
                  </div>

                  <button
                    class="icon-chat delete-audio"
                    (click)="cancelRecording()"
                    >
                    <i class="ri-delete-bin-6-line"></i>
                  </button>
                  <button
                    class="icon-chat"
                    (click)="toggleRecording(attendance._id)"
                    >
                    <i class="ri-stop-circle-line"></i>
                  </button>
                </div>
              }
              @if (isLoading && !audioUrl) {
                <div class="recording-preview">
                  <div
                    class="d-flex align-items-center mx-auto justify-content-center gap-2"
                    >
                    <mat-spinner
                      [diameter]="25"
                      [color]="'primary'"
                      mode="indeterminate"
                      />
                    <small class="text-muted">{{
                      "attendance.chat.loadingAudio" | translate
                    }}</small>
                  </div>
                </div>
              }
              @if (
                !contactIsBlocked($safeNavigationMigration(attendance?.contact), $safeNavigationMigration(attendance?.channel))
                ) {
                @if (!isMessageEdit) {
                  @if (attendance?.channel?.type === "Z_API") {
                    @if (!isRecording && !isLoading && !audioUrl) {
                      <button
                        class="icon-chat icon-chat-send"
                        (click)="openModalButtons()"
                        [matTooltip]="'attendance.chat.sendButtons' | translate"
                        >
                        <i class="mdi mdi-toggle-switch-off-outline"></i>
                      </button>
                    }
                  }

                  @if (!isRecording && !isLoading && !audioUrl) {
                    <button
                      class="icon-chat"
                      [matMenuTriggerFor]="mediaMenu"
                      [matTooltip]="'attendance.chat.attach' | translate"
                      >
                      <i class="mdi mdi-paperclip"></i>
                    </button>
                  }

                  @if (showContainerEmoji) {
                    <emoji-mart
                      id="emoji-container"
                      #emojiContainer
                      (emojiSelect)="addEmoji($event)"
                      [i18n]="translator"
                      [darkMode]="false"
                      />
                  }

                  @if (!isRecording && !isLoading && !audioUrl) {
                    <button
                      class="icon-chat"
                      (click)="toggleContainerEmoji()"
                      [matTooltip]="'attendance.chat.insertEmoji' | translate"
                      >
                      <i class="custom-insert-emoji"></i>
                    </button>
                  }

                  @if (!isRecording && !isLoading && !audioUrl) {
                    <button
                      class="icon-chat"
                      (click)="openModalResponse()"
                      [matTooltip]="'attendance.chat.quickReplies' | translate"
                      >
                      <i class="ri-question-answer-line"></i>
                    </button>
                  }
                }
                <!--
                Scheduling is hidden on RCS: a send scheduled for later may land after the
                24-hour service session has elapsed, which the backend then refuses
                (SPEC-2026-08 RF-15) — offering it would promise a delivery the channel
                cannot honour.
                -->
                @if (
                  attendance?.channel?.type !== "CLOUD_API" &&
                  attendance?.channel?.type !== "RCS_API" &&
                  !isMessageEdit
                  ) {
                  @if (!isRecording && !isLoading && !audioUrl) {
                    <button
                      class="icon-chat"
                      (click)="openModalScheduled(attendance)"
                    [matTooltip]="
                      'attendance.chat.scheduledMessage' | translate
                    "
                      >
                      <i class="mdi mdi-message-text-clock-outline"></i>
                    </button>
                  }
                }
                @if (attendance?.channel?.type === "CLOUD_API") {
                  @if (!isRecording && !isLoading && !audioUrl) {
                    <button
                      class="icon-chat"
                      (click)="openModalScheduledTemplate(attendance)"
                      >
                      <i class="mdi mdi-message-text-clock-outline"></i>
                    </button>
                  }
                }

                <textarea
                  #ta
                  rows="1"
                  class="form-control scrollbar"
                  [ngStyle]="audioUrl ? { display: 'none' } : {}"
                  [ngClass]="
                    isRecording || (isLoading && !audioUrl) ? 'is-hidden' : ''
                  "
                  formControlName="message"
                  [placeholder]="
                    'attendance.chat.messagePlaceholder' | translate
                  "
                  (keydown.enter)="enterEventPress($event, ta)"
                  (keydown)="handleKeydown($event)"
                  (focus)="focusInput()"
                  (blur)="blurInput()"
                  [disabled]="isRecording || (isLoading && !audioUrl)"
                ></textarea>

                @if (!isRecording && !isMessageEdit && attendance?.channel?.type !== "RCS_API") {
                  <button
                    [ngStyle]="audioUrl ? { display: 'none' } : {}"
                    class="icon-chat"
                    (click)="toggleRecording(attendance._id)"
                    >
                    <i class="mdi mdi-microphone"></i>
                  </button>
                }
              }
              @if (audioUrl) {
                <audio [src]="audioUrl" controls></audio>

                <div class="form-message__actions">
                  @if (!isRecording) {
                    <button
                      class="icon-chat delete-audio"
                      (click)="cancelPreview(attendance._id)"
                      >
                      <i class="ri-delete-bin-6-line"></i>
                    </button>
                  }
                </div>
              }
              @if (
                !contactIsBlocked($safeNavigationMigration(attendance?.contact), $safeNavigationMigration(attendance?.channel))
                ) {
                @if (!isRecording) {
                  <button
                    class="btn btn-success chat-send"
                    (click)="audioUrl ? sendAudio(attendance) : sendText()"
                    >
                    <i class="mdi mdi-send"></i>
                  </button>
                }
              }
            </div>
          </form>
        } @else if (attendance?.channel?.type === "CLOUD_API") {
          <!--
          Cloud API composes only inside the customer service window. Without one, the way
          forward is an approved template: the notice says why the composer is gone, whether
          the window elapsed or the customer never opened one.
          -->
          <div class="alert alert-warning py-2 mb-2" role="alert">
            <h5 class="alert-heading mt-0 mb-1">
              <i class="ri-time-line me-1"></i>
              {{ (cloudWindowExpired ? "attendance.chatHeader.window.closedTitle" : "attendance.chatHeader.window.noneTitle") | translate }}
            </h5>
            <p class="mb-2">
              @if (cloudWindowExpired) {
                {{
                "attendance.chatHeader.window.closedMessage"
                | translate
                : {
                date: attendance.expiredAt | timezoneDate: "DD/MM" : getTimezoneCompanyCurrentUser(),
                time: attendance.expiredAt | timezoneDate: "HH:mm" : getTimezoneCompanyCurrentUser(),
              }
              }}
            } @else {
              {{ "attendance.chatHeader.window.noneMessage" | translate }}
            }
          </p>
          <button type="button" class="btn btn-sm btn-primary" (click)="selectTemplate()">
            <i class="ri-layout-4-line me-1"></i>
            {{ "attendance.chatHeader.action.template" | translate }}
          </button>
        </div>
      } @else if (attendance?.channel?.type === "RCS_API") {
        <!--
        The two RCS states are distinct and must not share a message: an elapsed session
        is continued in place by the agent (ADR-003 — the button below resets the window
        to NONE on the backend), while a campaign-only channel is a configuration only an
        administrator can change.
        -->
        @if (isRCSChannelNotConversational()) {
          <div class="alert alert-warning py-2 mb-2" role="alert">
            <h5 class="alert-heading mt-0 mb-1">
              <i class="ri-error-warning-line me-1"></i>
              {{ "attendance.chat.rcsChannelNotConversationalTitle" | translate }}
            </h5>
            <p class="mb-0">
              {{ "attendance.chat.rcsChannelNotConversationalMessage" | translate }}
            </p>
          </div>
        } @else {
          <div class="alert alert-warning py-2 mb-2" role="alert">
            <h5 class="alert-heading mt-0 mb-1">
              <i class="ri-time-line me-1"></i>
              {{ "attendance.chat.rcsSessionExpiredTitle" | translate }}
            </h5>
            <p class="mb-2">
              {{ "attendance.chat.rcsSessionExpiredMessage" | translate }}
            </p>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              (click)="continueRcsSession()"
              >
              <i class="ri-chat-forward-line me-1"></i>
              {{ "attendance.chat.rcsContinueSession" | translate }}
            </button>
          </div>
        }
      }
    </div>
  }
}

<!-- <softphone-widget [attendance]="attendance$ | async" /> -->

<div [hidden]="true">
  <input
    type="file"
    id="inputImagefile"
    (change)="onSelectMedia('image', $event)"
    accept="image/jpeg, image/png"
    #inputImagefile
    />

  <input
    type="file"
    id="inputVideofile"
    (change)="onSelectMedia('video', $event)"
    accept="video/mp4, video/3gpp"
    #inputVideofile
    />

  <input
    type="file"
    id="inputDocfile"
    (change)="onSelectMedia('document', $event)"
    [accept]="documentAccept"
    #inputDocfile
    />

  <input
    type="file"
    id="inputAudiofile"
    (change)="onSelectMedia('audio', $event)"
    accept="audio/aac, audio/amr, audio/mpeg, audio/mp4, audio/ogg"
    #inputAudiofile
    />
</div>
</div>
